import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import { Box, Collapse } from "@mui/material";
import type { TFunction } from "i18next";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MOTION } from "../../theme/tokens";
import type { BreakContribution, WageBreakdownRow } from "../../utils/models";
import { prefersReducedMotion } from "../../utils/reducedMotion";
import { LedgerLine } from "../UIToolkit/molecules/LedgerLine";
import { formatTimeRange, moneyOrHidden } from "./consts";
import { RateTypeCell } from "./RateTypeCell";
import { RuleCard } from "./RuleCard/RuleCard";
import { RuleNameText } from "./RuleCard/RuleNameText";
import { useCompositionText } from "./useCompositionText";
import { WageSettingsLink } from "./WageSettingsLink";

type Props = {
	row: WageBreakdownRow;
	/** The rule that placed the break; `null` on a legacy row, or a recorded break no rule placed. */
	rule: BreakContribution | null;
	/** Whether the panel's "show details" toggle has the rule lines unfolded. */
	open: boolean;
	regionId: string;
	canViewWage: boolean;
	canOpenWageSettings: boolean;
};

/**
 * The break's two names: the rule that placed it (linked when the viewer can open Wage Settings) and the
 * general "Break" header — both opening that rule's row. A recorded break no rule placed has no card.
 */
const breakLabels = (
	t: TFunction,
	rowId: number,
	rule: BreakContribution | null,
	canOpenWageSettings: boolean,
): { ruleLabel: ReactNode; breakLabel: ReactNode } => {
	const card = rule === null ? undefined : <RuleCard subjects={[{ kind: "break", contribution: rule }]} dataTestId={`wage-breakdown-break-card-${rowId}`} />;
	const ruleLabel: ReactNode =
		rule === null ? (
			t("wageBreakdown.break.recorded")
		) : canOpenWageSettings ? (
			<WageSettingsLink
				target={{ section: "break-rules", rule: rule.name, ...(rule.ruleId === null ? {} : { ruleId: rule.ruleId }) }}
				label={rule.name}
				ariaLabel={t("wageBreakdown.openInWageSettings", { rateType: rule.name })}
				dataTestId={`wage-breakdown-rule-link-${rowId}-break`}
				emphasis="subtle"
				hoverCard={card}
			/>
		) : (
			<RuleNameText label={rule.name} card={card} dataTestId={`wage-breakdown-rule-name-${rowId}-break`} />
		);
	// The header names the break generally; its card is the same rule's row.
	const breakLabel: ReactNode =
		card === undefined ? (
			t("wageBreakdown.break.label")
		) : (
			<RuleNameText label={t("wageBreakdown.break.label")} card={card} dataTestId={`wage-breakdown-segment-name-${rowId}`} />
		);

	return { ruleLabel, breakLabel };
};

/**
 * A break is unpaid time. The header stays the general name "Break" — not a link — and the line under it
 * names the rule that placed it, linked, with why it applied.
 */
export const BreakSegmentLine: FC<Props> = ({ row, rule, open, regionId, canViewWage, canOpenWageSettings }) => {
	const { t } = useTranslation();
	const text = useCompositionText();
	const timeRange = formatTimeRange(row.startTime, row.endTime);
	const hours = t("wageBreakdown.segment.hours", { hours: row.totalHours.toFixed(2) });

	// A legacy row has no captured rule, so it keeps the old linked rate-type label and no detail line.
	if (rule === null && row.contributions === null) {
		return (
			<LedgerLine
				variant="deduction"
				leading={<FreeBreakfastOutlinedIcon sx={{ color: "text.disabled" }} />}
				label={<RateTypeCell rowId={row.id} rateType={row.rateType} linkable={canOpenWageSettings} includeAgency={false} />}
				detail={[timeRange, hours].join(text.separator)}
				rate={t("wageBreakdown.unpaid")}
				amount={moneyOrHidden(0, canViewWage)}
				dataTestId={`wage-breakdown-subtable-row-${row.id}`}
			/>
		);
	}

	const { ruleLabel, breakLabel } = breakLabels(t, row.id, rule, canOpenWageSettings);

	return (
		<Box component="li" data-testid={`wage-breakdown-subtable-row-${row.id}`} sx={{ listStyle: "none", m: 0, p: 0 }}>
			<LedgerLine
				component="div"
				leading={<FreeBreakfastOutlinedIcon sx={{ color: "text.disabled" }} />}
				label={breakLabel}
				detail={[timeRange, hours].join(text.separator)}
				rate={t("wageBreakdown.unpaid")}
				amount={moneyOrHidden(0, canViewWage)}
			/>
			<Collapse
				id={regionId}
				in={open}
				timeout={prefersReducedMotion() ? 0 : { enter: MOTION.DURATION.ENTRANCE_MD, exit: MOTION.DURATION.INLINE_EXIT }}
				easing={MOTION.EASE_OUT}
				sx={{ "@media print": { display: "none" } }}
				data-testid={`wage-breakdown-composition-${row.id}`}
			>
				<Box component="ul" sx={{ m: 0, p: 0 }}>
					{/* No figure: the break's length is already on its line above, and a "−0:30" here read as money. */}
					<LedgerLine variant="component" label={ruleLabel} detail={rule === null ? undefined : text.breakDetail(rule)} />
				</Box>
			</Collapse>
		</Box>
	);
};
